import { useEffect, useRef, useState } from "react";

const supportsIntersectionObserver = () =>
  typeof window !== "undefined" && typeof window.IntersectionObserver === "function";

/**
 * Tracks whether an element has ever scrolled into view, so expensive content
 * (images, previews) can be deferred until it is actually needed.
 *
 * The result is sticky: once the element has been seen the observer disconnects
 * and the flag never flips back, so scrolling away and back never unloads or
 * re-requests anything.
 *
 * When `IntersectionObserver` is unavailable (jsdom, older browsers) the flag
 * starts `true`, so callers fall back to loading eagerly.
 *
 * Pass `root` when the element lives inside a nested scroll container. With the
 * default viewport root the browser clips the element's rect at the container's
 * edge, which silently prevents `rootMargin` from prefetching ahead of scroll.
 *
 * @param {Object} [options]
 * @param {React.RefObject<HTMLElement>} [options.root] - Ref to the scrolling ancestor. Defaults to the viewport.
 * @param {string} [options.rootMargin="0px"] - Margin around the root, e.g. "400px" to load a screen ahead.
 * @returns {[React.RefObject<HTMLElement>, boolean]} Ref to attach to the element, and whether it has been seen.
 */
const useInViewport = ({ root = null, rootMargin = "0px" } = {}) => {
  const ref = useRef(null);
  const [hasBeenVisible, setHasBeenVisible] = useState(() => !supportsIntersectionObserver());

  useEffect(() => {
    if (hasBeenVisible) return undefined;

    const element = ref.current;
    if (!element) return undefined;

    const observer = new window.IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setHasBeenVisible(true);
          observer.disconnect();
        }
      },
      { root: root?.current ?? null, rootMargin },
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [hasBeenVisible, root, rootMargin]);

  return [ref, hasBeenVisible];
};

export default useInViewport;
